import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  RhythmCanvasPayload,
  RhythmCanvasRuntime,
  RhythmSlotFeedback,
} from '../../core/types/payloads/rhythm_canvas.types';
import type { RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RhythmicCell } from '../../core/music/rhythmicCellEngine';
import type { MusicalToken } from '../../core/types/music';
import {
  normalizeRhythmPattern,
  normalizeTokenArray,
  tokenBeats,
  tokenToLabel,
} from '../../core/music/tokens';
import { buildBarBeatStructure } from '../../core/music/rhythmicCellEngine';
import {
  buildRhythmCellMcqOptions,
  correctCellForBeat,
} from '../../core/music/rhythmCellMcq';

function defaultPrompt(taskKind: RhythmCanvasPayload['taskKind']): string {
  switch (taskKind) {
    case 'order':
      return 'Ordena las figuras para completar el compás:';
    case 'identify':
      return 'Selecciona la figura rítmica correcta:';
    case 'practice':
    case 'transcribe':
      return 'Escucha el patrón y transcribe cada figura en los casilleros:';
    case 'syllables':
      return 'Completa las sílabas rítmicas bajo cada figura:';
    case 'match':
      return 'Une cada figura con su nombre:';
    default:
      return 'Completa la figura que falta en la secuencia:';
  }
}

function resolveBlankIndices(
  taskKind: RhythmCanvasPayload['taskKind'],
  n: number,
  missingIndex?: number,
  blankIndices?: number[],
): number[] {
  if (Array.isArray(blankIndices) && blankIndices.length) {
    return blankIndices.filter((i) => i >= 0 && i < n);
  }
  if (taskKind === 'transcribe' || taskKind === 'practice') {
    return Array.from({ length: n }, (_, i) => i);
  }
  if (taskKind === 'syllables') {
    return Array.from({ length: n }, (_, i) => i);
  }
  if (taskKind === 'complete' && typeof missingIndex === 'number' && missingIndex >= 0 && missingIndex < n) {
    return [missingIndex];
  }
  if (n > 0) return [n - 1];
  return [];
}

export function validateRhythmCanvasPayload(raw: unknown): RhythmCanvasPayload {
  if (!raw || typeof raw !== 'object') throw new Error('RhythmCanvas payload must be an object');
  const data = raw as Partial<RhythmCanvasPayload>;
  if (!data.title) throw new Error('RhythmCanvas requires title');

  const rhythmPattern = normalizeRhythmPattern(data.rhythmPattern);
  const patternTokens =
    rhythmPattern?.pattern ??
    normalizeTokenArray(data.originalBarTokens ?? data.patternTokens);
  if (patternTokens.length < 1) throw new Error('RhythmCanvas requires patternTokens');

  const timeSignature =
    rhythmPattern?.timeSignature ??
    (['4/4', '3/4', '2/4'].includes(String(data.timeSignature))
      ? (String(data.timeSignature) as RhythmCanvasPayload['timeSignature'])
      : '4/4');

  const taskKind =
    data.taskKind === 'order' ||
    data.taskKind === 'identify' ||
    data.taskKind === 'match' ||
    data.taskKind === 'practice' ||
    data.taskKind === 'transcribe' ||
    data.taskKind === 'syllables'
      ? data.taskKind
      : 'complete';

  const n = patternTokens.length;
  const rhythmMethod =
    data.rhythmMethod === 'takadimi' || data.rhythmMethod === 'kodaly' ? data.rhythmMethod : 'tradicional';

  const rhythmDots = Array.isArray(data.rhythmDots)
    ? data.rhythmDots.map(Number).filter((i) => Number.isFinite(i) && i >= 0 && i < n)
    : undefined;

  const beatCells = Array.isArray(data.beatCells) ? (data.beatCells as RhythmicCell[]) : undefined;
  const rhythmicCells = Array.isArray(data.rhythmicCells)
    ? (data.rhythmicCells as RhythmicCell[])
    : undefined;
  const beatsPerBar =
    typeof data.beatsPerBar === 'number' && data.beatsPerBar >= 1
      ? data.beatsPerBar
      : beatCells?.length;

  const dottedForEngine = rhythmDots;

  let resolvedBeatCells = beatCells;
  let resolvedRhythmicCells = rhythmicCells;
  let resolvedBeatsPerBar = beatsPerBar;
  if (!resolvedBeatCells?.length) {
    const structure = buildBarBeatStructure(patternTokens, timeSignature, {
      dottedIndices: dottedForEngine,
    });
    resolvedBeatCells = structure.beatCells;
    resolvedRhythmicCells = structure.cells;
    resolvedBeatsPerBar = structure.beatsPerBar;
  }

  const slotCount = resolvedBeatCells?.length ?? n;
  const missingIndex =
    typeof data.missingIndex === 'number' && data.missingIndex >= 0 && data.missingIndex < slotCount
      ? data.missingIndex
      : taskKind === 'complete'
        ? Math.min(1, Math.max(slotCount - 1, 0))
        : undefined;

  const correctOrder = Array.isArray(data.correctOrder)
    ? data.correctOrder.map(Number).filter((v) => v >= 0 && v < n)
    : Array.from({ length: n }, (_, i) => i);

  const matchPairs = Array.isArray(data.matchPairs)
    ? data.matchPairs
        .map((pair, i) => {
          const row = pair as { id?: string; token?: string; label?: string };
          const token = normalizeTokenArray([row.token])[0] ?? patternTokens[i] ?? 'Q';
          return {
            id: String(row.id || `p${i + 1}`),
            token,
            label: String(row.label || tokenToLabel(token, rhythmMethod)).trim(),
          };
        })
        .filter((p) => p.label)
    : undefined;

  const blankIndicesResolved = resolveBlankIndices(
    taskKind,
    slotCount,
    missingIndex,
    data.blankIndices,
  );

  const resolvedMissingIndex =
    taskKind === 'complete' ? (blankIndicesResolved[0] ?? missingIndex) : missingIndex;

  let cellOptions = Array.isArray(data.cellOptions)
    ? data.cellOptions
        .map((row) => {
          const opt = row as { id?: string; label?: string; token?: string; takadimi?: string };
          const token = normalizeTokenArray([opt.token])[0] ?? 'Q';
          return {
            id: String(opt.id || token),
            label: String(opt.label || tokenToLabel(token, rhythmMethod)).trim(),
            token,
            takadimi: opt.takadimi ? String(opt.takadimi) : undefined,
          };
        })
        .filter((o) => o.label)
    : undefined;

  let correctCellOptionIndex =
    typeof data.correctCellOptionIndex === 'number'
      ? Math.max(0, data.correctCellOptionIndex)
      : undefined;

  if (
    !cellOptions?.length &&
    resolvedBeatCells?.length &&
    (taskKind === 'complete' || taskKind === 'identify')
  ) {
    const beatIdx =
      typeof resolvedMissingIndex === 'number' && resolvedMissingIndex >= 0
        ? resolvedMissingIndex
        : 0;
    const correctCell = correctCellForBeat(resolvedBeatCells, beatIdx);
    if (correctCell) {
      const built = buildRhythmCellMcqOptions({
        correctCell,
        beatCells: resolvedBeatCells,
        maxOptions: 4,
        rhythmMethod,
        shuffleSeed: beatIdx,
      });
      cellOptions = built.options;
      correctCellOptionIndex = built.correctIndex;
    }
  }

  const legacyOptions = Array.isArray(data.options)
    ? normalizeTokenArray(data.options)
    : cellOptions?.map((o) => o.token);

  return {
    title: String(data.title),
    timeSignature,
    patternTokens,
    rhythmPattern: rhythmPattern ?? { timeSignature, pattern: patternTokens, missingIndex },
    missingIndex: resolvedMissingIndex,
    taskKind,
    prompt: String(data.prompt || defaultPrompt(taskKind)).trim(),
    rhythmMethod,
    graphicMode: data.graphicMode === 'symbols-labels' ? 'symbols-labels' : 'symbols',
    metronomeBpm: Number(data.metronomeBpm) || 72,
    correctOrder: correctOrder.length === n ? correctOrder : Array.from({ length: n }, (_, i) => i),
    options: legacyOptions,
    correctOptionIndex:
      typeof data.correctOptionIndex === 'number'
        ? Math.max(0, data.correctOptionIndex)
        : correctCellOptionIndex ?? 0,
    cellOptions,
    correctCellOptionIndex: correctCellOptionIndex ?? 0,
    matchPairs: matchPairs?.length ? matchPairs : undefined,
    targetBarNumber:
      typeof data.targetBarNumber === 'number' && data.targetBarNumber >= 1
        ? data.targetBarNumber
        : undefined,
    originalBarTokens: patternTokens,
    rhythmDots,
    blankIndices: blankIndicesResolved,
    hidePattern:
      data.hidePattern === true ||
      ((taskKind === 'practice' || taskKind === 'transcribe') && data.hidePattern !== false),
    beatCells: resolvedBeatCells,
    rhythmicCells: resolvedRhythmicCells,
    beatsPerBar: resolvedBeatsPerBar,
  };
}

function normalizeText(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function slotResponseMap(response: unknown): Record<string, string> {
  if (response && typeof response === 'object' && !Array.isArray(response)) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(response as Record<string, unknown>)) {
      out[k] = String(v ?? '');
    }
    return out;
  }
  return {};
}

export function scoreRhythmCanvas(
  payload: RhythmCanvasPayload,
  response: unknown,
): { correct: number; total: number; feedback: Record<string, RhythmSlotFeedback> } {
  const feedback: Record<string, RhythmSlotFeedback> = {};
  const method = payload.rhythmMethod ?? 'tradicional';
  const pattern = payload.patternTokens;
  const beatCells = payload.beatCells;
  const useBeatMode = Boolean(beatCells?.length);
  const n = useBeatMode ? beatCells!.length : pattern.length;

  if (payload.taskKind === 'identify') {
    const idx = Number(response);
    const correctIdx =
      payload.cellOptions?.length && payload.correctCellOptionIndex != null
        ? payload.correctCellOptionIndex
        : payload.correctOptionIndex;
    const ok = idx === correctIdx;
    return { correct: ok ? 1 : 0, total: 1, feedback: { '0': ok ? 'correct' : 'wrong' } };
  }

  const slots = slotResponseMap(response);
  const blankIndices =
    payload.blankIndices?.length
      ? payload.blankIndices
      : resolveBlankIndices(payload.taskKind, n, payload.missingIndex);

  let correct = 0;
  for (const i of blankIndices) {
    if (useBeatMode && beatCells?.[i]) {
      const cell = beatCells[i]!;
      const given = slots[String(i)] ?? '';
      const ok =
        payload.taskKind === 'syllables'
          ? normalizeText(given) === normalizeText(cell.takadimi) ||
            normalizeText(given) === normalizeText(tokenToLabel(cell.tokens[0] ?? 'Q', method))
          : given === cell.id ||
            normalizeTokenArray([given])[0] === (cell.tokens[0] ?? 'Q') ||
            normalizeText(given) === normalizeText(cell.name);
      feedback[String(i)] = ok ? 'correct' : 'wrong';
      if (ok) correct += 1;
      continue;
    }

    const expected =
      payload.taskKind === 'syllables'
        ? tokenToLabel(pattern[i], method)
        : pattern[i];
    const given = slots[String(i)] ?? '';
    const ok =
      payload.taskKind === 'syllables'
        ? normalizeText(given) === normalizeText(expected) ||
          normalizeText(given) === normalizeText(tokenToLabel(pattern[i], 'tradicional'))
        : normalizeTokenArray([given])[0] === pattern[i] ||
          normalizeText(given) === normalizeText(tokenToLabel(pattern[i], method));
    feedback[String(i)] = ok ? 'correct' : 'wrong';
    if (ok) correct += 1;
  }

  return { correct, total: blankIndices.length || 1, feedback };
}

export function createInitialRhythmCanvasRuntime(
  _payload: RhythmCanvasPayload,
  _routing: RoutingMode,
): RhythmCanvasRuntime {
  return { responses: {}, finalized: {}, aggregates: { submitted: 0 } };
}

export const rhythmCanvasLogic: ActivityLogicPlugin<'rhythm_canvas'> = {
  mode: 'rhythm_canvas',
  validatePayload: validateRhythmCanvasPayload,
  createInitialRuntime: createInitialRhythmCanvasRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    const pid = participant.participantId;

    if (input.action === 'FILL') {
      const data = input.data as {
        value?: string | number;
        slotIndex?: number;
        slots?: Record<string, string>;
      };

      if (data.slots && typeof data.slots === 'object') {
        const prev = slotResponseMap(ctx.runtime.responses[pid]);
        return {
          runtime: {
            ...ctx.runtime,
            responses: { ...ctx.runtime.responses, [pid]: { ...prev, ...data.slots } },
          },
        };
      }

      if (typeof data.slotIndex === 'number') {
        const prev = slotResponseMap(ctx.runtime.responses[pid]);
        return {
          runtime: {
            ...ctx.runtime,
            responses: {
              ...ctx.runtime.responses,
              [pid]: { ...prev, [String(data.slotIndex)]: String(data.value ?? '') },
            },
          },
        };
      }

      return {
        runtime: {
          ...ctx.runtime,
          responses: { ...ctx.runtime.responses, [pid]: data.value ?? '' },
        },
      };
    }

    if (input.action === 'SUBMIT') {
      const { correct, total, feedback } = scoreRhythmCanvas(ctx.payload, ctx.runtime.responses[pid]);
      const finalized = { ...ctx.runtime.finalized, [pid]: true };
      const submitted = Object.keys(finalized).filter((id) => finalized[id]).length;
      const scores = { ...(ctx.runtime.scores ?? {}), [pid]: correct };
      const slotFeedback = { ...(ctx.runtime.slotFeedback ?? {}), [pid]: feedback };
      return {
        runtime: {
          ...ctx.runtime,
          finalized,
          scores,
          slotFeedback,
          aggregates: { submitted, correct: total ? Math.round((correct / total) * 100) : 0 },
        },
      };
    }

    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoRhythmCanvasPayload(): RhythmCanvasPayload {
  return validateRhythmCanvasPayload({
    title: 'Compás rítmico',
    timeSignature: '4/4',
    patternTokens: ['Q', 'E', 'Q', 'E'],
    taskKind: 'complete',
    missingIndex: 2,
  });
}

export function rhythmCanvasBeatCount(payload: RhythmCanvasPayload): number {
  if (payload.beatsPerBar && payload.beatsPerBar >= 1) return payload.beatsPerBar;
  if (payload.beatCells?.length) return payload.beatCells.length;
  return payload.patternTokens.reduce((sum, t) => sum + tokenBeats(t), 0);
}

export function rhythmCanvasPaletteTokens(pattern: MusicalToken[]): MusicalToken[] {
  const base: MusicalToken[] = ['Q', 'E', 'H', 'W', 'S', 'R_Q'];
  const fromPattern = [...new Set(pattern)];
  return [...new Set([...fromPattern, ...base])];
}
